<template>
  <div class="my-3">
    <div
      class="font-medium font-weight-bold"
    >
      Endorsements
    </div>

    <base-loader v-if="isLoading" />

    <template v-if="!isLoading">
      <v-chip
        class="mt-2 ml-2 clickable"
        @click="openDialog"
      >
        Add Endorsement

        <v-avatar right>
          <v-icon>mdi-plus</v-icon>
        </v-avatar>
      </v-chip>

      <div class="mt-6">
        <draggable
          v-model="endorsementsClone"
          handle=".handle"
          v-bind="dragOptions"
          @change="onReorder()"
        >
          <transition-group>
            <div
              v-for="e in endorsementsClone"
              :key="e.id"
              class="endorsement"
            >
              <div
                class="handle"
                style="height: 100%; width: 100%;"
              >
                <v-icon>mdi-drag-horizontal-variant</v-icon>
              </div>
              <button
                :aria-label="'Edit Endorsement by ' + e.endorser"
                :disabled="e.id === -1"
                class="btn"
                @click="onEdit(e)"
              >
                <div
                  class="mb-1 d-flex "
                >
                  <ReadonlyQuill :value="e.endorser" />
                  <div style="flex: 1" />
                  <span :class="e.isHidden ? 'red--text' : 'gray--text'">{{ e.isHidden ? 'Hidden' : 'Active' }}</span>
                </div>

                <ReadonlyQuill :value="e.endorsementText" />
              </button>

              <button
                class="end"
                :aria-label="'delete endorsement by ' + e.endorser"
                :disabled="e.id === -1"
                @click="deleteEndorsement(e)"
              >
                <v-icon>mdi-close</v-icon>
              </button>

              <div class="loading-indicator">
                <base-loader v-if="e.id === -1" />
              </div>
            </div>
          </transition-group>
        </draggable>
      </div>
    </template>

    <v-dialog
      v-model="isDialogOpen"
      persistent
    >
      <v-card>
        <v-card-title class="bpg-card-title-block d-flex justify-space-between">
          <div class="font-weight-extra">
            {{ editingEndorsement ? 'Edit Endorsement' : 'Add Endorsement' }}
          </div>

          <v-btn
            icon
            aria-label="close"
            @click="closeDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <div class="pa-6 font-default-size">
          <div
            class="mb-3"
            style="font-weight: 600"
          >
            Endorser *
          </div>

          <quill-editor
            :value="editingEndorsement ? editingEndorsement.endorser : newEndorsement.endorser"
            :options="endorserEditorOption"
            @input="onTitleChange($event)"
          />

          <div
            class="mb-3 mt-1"
            style="font-weight: 600"
          >
            Endorsement Text *
          </div>
          <quill-editor
            :value="editingEndorsement ? editingEndorsement.endorsementText : newEndorsement.endorsementText"
            :options="editorOption"
            @input="onTextChange($event)"
          />
          <v-checkbox
            label="Is Active"
            :input-value="editingEndorsement ? !editingEndorsement.isHidden : !newEndorsement.isHidden"
            aria-label="Is Active"
            @change="onIsActiveChange($event)"
          />
        </div>

        <div class="d-flex pa-6 stripe">
          <v-btn
            text
            @click="closeDialog"
          >
            <span
              style="font-weight: 600"
              class="primary--text"
            >
              CANCEL</span>
          </v-btn>

          <div style="flex: 1" />

          <base-button
            :text="editingEndorsement ? 'Edit' : 'Add'"
            :disabled="invalid"
            @click="add"
          />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import * as cloneDeep from 'lodash.clonedeep'
import { quillEditor } from 'vue-quill-editor'
import ReadonlyQuill from '../../shared/readonly-quill.vue'
import draggable from 'vuedraggable'

export default {
  name: "ProductEndorsements",
  components: { quillEditor, ReadonlyQuill, draggable },
  props: {
    endorsements: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean
    }
  },
  data: () => ({
    isDialogOpen: false,
    endorsementsClone: [],
    newEndorsement: {
      endorser: '',
      endorsementText: '',
      isHidden: false
    },
    editingEndorsement: null,
    readonlyQuill: {
      modules: {
        toolbar: false
      },
      readOnly: true
    },
    endorserEditorOption: {
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike']
        ]
      }
    },
    editorOption: {
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block', 'link'],
          [{ 'header': 1 }, { 'header': 2 }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'script': 'sub' }, { 'script': 'super' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'direction': 'rtl' }],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          [{ 'align': [] }]
        ]
      }
    }
  }),
  computed: {
    dragOptions () {
      return {
        animation: 200,
        group: "description",
        disabled: false,
        ghostClass: "ghost"
      }
    },
    invalid () {
      const e = this.editingEndorsement ?? this.newEndorsement
      return !(e.endorser && e.endorsementText)
    }
  },
  watch: {
    endorsements: {
      handler () {
        this.endorsementsClone = cloneDeep(this.endorsements)
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    openDialog () {
      this.isDialogOpen = true
    },
    closeDialog () {
      this.isDialogOpen = false
      this.newEndorsement = {
        endorser: '',
        endorsementText: ''
      }
      this.editingEndorsement = null
    },
    add () {
      if (this.editingEndorsement) {
        this.$emit('editEndorsement', this.editingEndorsement)
      } else {
        this.$emit('addEndorsement', this.newEndorsement)
      }
      this.closeDialog()
    },
    deleteEndorsement (endorsement) {
      this.$emit('deleteEndorsement', endorsement)
    },
    onEdit (e) {
      if (e.id === -1) {
        return
      }
      this.editingEndorsement = cloneDeep(e)
      this.isDialogOpen = true
    },
    onTitleChange (txt) {
      (this.editingEndorsement ?? this.newEndorsement).endorser = txt
    },
    onTextChange (txt) {
      (this.editingEndorsement ?? this.newEndorsement).endorsementText = txt
    },
    onIsActiveChange (isActive) {
      (this.editingEndorsement ?? this.newEndorsement).isHidden = !isActive
    },
    onReorder () {
      this.$emit('reorder', this.endorsementsClone)
    }
  }
}
</script>

<style lang="scss" scoped>
@import './src/styles/variables';
.stripe {
    background-color: $even-lighter-grey;
}

.endorsement {
    border-top: 1px solid $light-grey;
    display: grid;
    grid-template-columns: 50px 1fr 50px;
    background-color: $white;
    position: relative;

    div {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2; /* number of lines to show */
      line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .handle {
      display: flex;
      align-items: center;
      cursor:move;
      justify-content: center;
      &:hover {
        background-color: $even-lighter-grey;
      }

    }
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.endorsement:last-child {
    border-bottom: 1px solid $light-grey;
}

.btn {
  text-align: start;
  &:hover {
    background-color: $even-lighter-grey;
  }
     padding: 24px 0;
}

.end {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: $even-lighter-grey;
  }
}

.ghost {
  opacity: 0;
}
</style>
